import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInvoices, calcTotals, INVOICE_STATUSES } from '../../context/InvoicesContext';
import { fmtMoney, fmtDateFull, daysUntil } from '../../lib/format';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Receipt, FileText, ExternalLink } from 'lucide-react';
import InitialsAvatar from '../InitialsAvatar';

const statusMeta = (id) => INVOICE_STATUSES.find((s) => s.id === id) || INVOICE_STATUSES[0];

const ActiveInvoicesButton = () => {
  const { invoices, brandMap, talentMap } = useInvoices();
  const [open, setOpen] = useState(false);

  // "active" = any invoice not yet marked paid
  const active = useMemo(() => {
    return (invoices || [])
      .filter((i) => i.status !== 'paid')
      .sort((a, b) => (a.due_date || '9999').localeCompare(b.due_date || '9999'));
  }, [invoices]);

  const total = useMemo(() => active.reduce((s, i) => s + calcTotals(i).total, 0), [active]);

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-pink-200 text-pink-700 hover:bg-pink-50 hover:text-pink-800 font-semibold"
        data-testid="active-invoices-button"
      >
        <Receipt className="w-4 h-4 mr-1.5" /> active invoices
        <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-pink-500 text-white text-[11px] font-semibold">{active.length}</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="active-invoices-dialog">
          <DialogHeader className="px-5 py-4 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <Receipt className="w-5 h-5 text-pink-600" /> active invoices
              <span className="text-sm font-normal text-slate-400">— not yet marked paid</span>
            </DialogTitle>
            <DialogDescription className="sr-only">Invoices that have not been marked as paid.</DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto">
            {active.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-400">no active invoices — everything is paid 🎉</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 sticky top-0 z-10">
                  <tr>
                    <th className="text-left font-medium px-4 py-2.5">invoice</th>
                    <th className="text-left font-medium px-4 py-2.5">brand · talent</th>
                    <th className="text-right font-medium px-4 py-2.5">total</th>
                    <th className="text-left font-medium px-4 py-2.5">due</th>
                    <th className="text-left font-medium px-4 py-2.5">status</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {active.map((inv) => {
                    const brand = brandMap[inv.brand_id];
                    const talent = talentMap[inv.talent_id];
                    const meta = statusMeta(inv.status);
                    const dueIn = daysUntil(inv.due_date);
                    const overdue = inv.status !== 'paid' && dueIn !== null && dueIn < 0;
                    return (
                      <tr key={inv.id} className="border-t border-slate-100 hover:bg-pink-50/40" data-testid={`active-invoice-row-${inv.id}`}>
                        <td className="px-4 py-2.5">
                          <Link to={`/invoicing/${inv.id}`} onClick={() => setOpen(false)} className="inline-flex items-center gap-1.5 font-medium text-pink-700 hover:text-pink-800">
                            <FileText className="w-3.5 h-3.5" /> {inv.number}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2 min-w-0">
                            {talent && <InitialsAvatar name={talent.name} size={22} />}
                            <span className="text-slate-700 truncate">{brand?.name || '—'} <span className="text-slate-400">×</span> {talent?.name || '—'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{fmtMoney(calcTotals(inv).total, inv.currency)}</td>
                        <td className={`px-4 py-2.5 text-xs ${overdue ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                          {inv.due_date ? fmtDateFull(inv.due_date) : '—'}
                          {overdue && <span className="ml-1">(overdue)</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: `${meta.color}1f`, color: meta.color }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />{meta.label}
                          </span>
                        </td>
                        <td className="px-2 py-2.5">
                          <Link to={`/invoicing/${inv.id}`} onClick={() => setOpen(false)} className="text-slate-300 hover:text-pink-600" title="open invoice">
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {active.length > 0 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50">
              <span className="text-xs text-slate-500">{active.length} active invoice{active.length === 1 ? '' : 's'}</span>
              <span className="text-sm font-semibold text-slate-800">outstanding total {fmtMoney(total)}</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActiveInvoicesButton;
